import React from "react";
import { Lock, LockOpen } from "lucide-react";

const fmt = (n, d = 3) =>
  n == null || Number.isNaN(n) ? "—" : Number(n).toFixed(d);

const fmt$ = (n) => {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "−";
  const abs = Math.abs(n);
  if (abs >= 100) return `${sign}$${Math.round(abs).toLocaleString()}`;
  return `${sign}$${abs.toFixed(2)}`;
};

const GreekCell = ({ label, value, hint, color = "text-white" }) => (
  <div className="bg-[#0a0a0c] border border-white/10 rounded-lg px-3 py-2.5">
    <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">{label}</div>
    <div className={`num text-base font-medium mt-1 ${color}`}>{value}</div>
    {hint && (
      <div className="text-[10px] text-white/45 mt-0.5 leading-tight">{hint}</div>
    )}
  </div>
);

export const GreeksTable = ({
  greeks, netPremium, breakevens, pop, maxProfit, maxLoss,
  legs = [], pricing = null, earnings = null,
  lockedEntry = null, onLock, onUnlock,
}) => {
  if (!greeks) return null;
  const isCredit = netPremium != null && netPremium < 0;

  // Index the locked snapshot by leg signature so we can join it to the
  // current legs row-by-row.
  const lockedBySig = lockedEntry
    ? Object.fromEntries(lockedEntry.legs.map((l) => [l.sig, l]))
    : {};
  // Compute live $ and % return on the locked position by netting the
  // current mid against the entry mid for each option leg (qty + sign aware).
  const livePnl = (() => {
    if (!lockedEntry) return null;
    let pnl = 0;
    let entryAbs = 0;
    legs.forEach((leg) => {
      if (leg.type === "stock") return;
      const sig = `${leg.action}-${leg.type}-${leg.strike}-${leg.dte}`;
      const snap = lockedBySig[sig];
      if (!snap || snap.entry_mid == null || leg.mid == null) return;
      const sign = leg.action === "BUY" ? 1 : -1;
      const mult = 100 * (leg.qty || 1);
      pnl += (leg.mid - snap.entry_mid) * sign * mult;
      entryAbs += Math.abs(snap.entry_mid) * mult;
    });
    const pct = entryAbs > 0 ? (pnl / entryAbs) * 100 : null;
    return { pnl, pct };
  })();
  const pricingBadge = pricing?.source && (
    <span
      className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border"
      data-testid="forge-pricing-source"
      style={
        pricing.source === "live"
          ? { color: "#00C805", borderColor: "#00C80555", background: "#00C80512" }
          : pricing.source === "mixed"
            ? { color: "#FFD60A", borderColor: "#FFD60A55", background: "#FFD60A12" }
            : { color: "#A98CFF", borderColor: "#A98CFF55", background: "#A98CFF12" }
      }
      title={
        pricing.source === "live"
          ? "Premiums sourced from live option-chain mids"
          : pricing.source === "mixed"
            ? "Some legs live, some modelled (no chain quote for that strike/expiry)"
            : "Premiums modelled via Black-Scholes (no live chain available)"
      }
    >
      ● {pricing.source} {pricing.symbol ? `· ${pricing.symbol}` : ""}
    </span>
  );
  return (
    <div className="space-y-3" data-testid="greeks-table">
      <div className="flex flex-wrap items-center justify-between gap-2 -mb-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          Position Summary
        </div>
        <div className="flex items-center gap-2">
          {pricingBadge}
          {/* Lock entry: snapshot the current leg mids so the table shows
              live P/L vs that frozen entry from here on. */}
          {lockedEntry ? (
            <button
              type="button"
              onClick={onUnlock}
              data-testid="forge-unlock-entry"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-[#FFD60A]/50 text-[#FFD60A] hover:bg-[#FFD60A]/10 transition"
              title="Discard locked entry"
            >
              <LockOpen className="w-3 h-3" /> Locked
            </button>
          ) : (
            <button
              type="button"
              onClick={onLock}
              disabled={!legs?.length || !onLock}
              data-testid="forge-lock-entry"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/35 disabled:opacity-40 transition"
              title="Snapshot the current leg mids as your entry"
            >
              <Lock className="w-3 h-3" /> Lock entry
            </button>
          )}
        </div>
      </div>

      {/* Live P/L banner — only renders once entry is locked. */}
      {lockedEntry && livePnl && (
        <div
          className="rounded-lg border px-3 py-2 flex flex-wrap items-center justify-between gap-2"
          data-testid="forge-live-pnl"
          style={{
            borderColor: livePnl.pnl >= 0 ? "#00C80555" : "#FF3B3055",
            background: livePnl.pnl >= 0 ? "#00C80508" : "#FF3B3008",
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">
              Live vs locked entry
            </span>
            <span className="text-[10px] text-white/40 num">
              {new Date(lockedEntry.ts).toLocaleString(undefined, {
                hour: "2-digit", minute: "2-digit", month: "short", day: "numeric",
              })}
            </span>
          </div>
          <div className="flex items-baseline gap-3">
            <span
              className="text-lg num font-medium"
              style={{ color: livePnl.pnl >= 0 ? "#00C805" : "#FF3B30" }}
            >
              {fmt$(livePnl.pnl)}
            </span>
            {livePnl.pct != null && (
              <span
                className="text-[12px] num"
                style={{ color: livePnl.pnl >= 0 ? "#00C805" : "#FF3B30" }}
              >
                {livePnl.pct >= 0 ? "+" : ""}{livePnl.pct.toFixed(2)}%
              </span>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <GreekCell
          label="Net Premium"
          value={fmt$(netPremium)}
          hint={isCredit ? "Credit received" : "Debit paid"}
          color={isCredit ? "text-[#00C805]" : "text-[#FFD60A]"}
        />
        <GreekCell
          label="POP"
          value={pop == null ? "—" : `${pop}%`}
          hint="Prob. of Profit"
          color={(pop ?? 0) >= 65 ? "text-[#00C805]" : (pop ?? 0) >= 45 ? "text-[#FFD60A]" : "text-[#FF7A33]"}
        />
        <GreekCell
          label="Max Profit"
          value={maxProfit == null ? "∞" : fmt$(maxProfit)}
          color="text-[#00C805]"
        />
        <GreekCell
          label="Max Loss"
          value={maxLoss == null ? "−∞" : fmt$(maxLoss)}
          color="text-[#FF3B30]"
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <GreekCell label="Δ Delta" value={fmt(greeks.delta, 2)} hint="Directional bias" />
        <GreekCell label="Γ Gamma" value={fmt(greeks.gamma, 3)} hint="Delta sensitivity" />
        <GreekCell label="Θ Theta" value={fmt(greeks.theta, 2)} hint="Daily decay" color={greeks.theta >= 0 ? "text-[#00C805]" : "text-[#FF7A33]"} />
        <GreekCell label="ν Vega" value={fmt(greeks.vega, 2)} hint="Vol sensitivity" color={greeks.vega >= 0 ? "text-[#00C805]" : "text-[#FF7A33]"} />
        <GreekCell label="ρ Rho" value={fmt(greeks.rho, 2)} hint="Rate sensitivity" />
      </div>

      {/* Per-leg breakdown — chain bid/ask/mid + IV + greeks source. */}
      {!!legs.length && (
        <div className="overflow-x-auto" data-testid="legs-breakdown">
          <table className="w-full text-[11px] border border-white/10 rounded-lg overflow-hidden">
            <thead className="text-white/40 uppercase tracking-[0.16em] text-[9px] bg-[#0a0a0c]">
              <tr>
                <th className="text-left px-2 py-1.5">Leg</th>
                <th className="text-right px-2 py-1.5">Strike</th>
                <th className="text-right px-2 py-1.5">Bid</th>
                <th className="text-right px-2 py-1.5">Mid</th>
                <th className="text-right px-2 py-1.5">Ask</th>
                <th className="text-right px-2 py-1.5">IV</th>
                <th className="text-right px-2 py-1.5">Δ</th>
                <th className="text-right px-2 py-1.5">Cost</th>
                {lockedEntry && <th className="text-right px-2 py-1.5">Entry</th>}
                {lockedEntry && <th className="text-right px-2 py-1.5">P/L</th>}
              </tr>
            </thead>
            <tbody>
              {legs.map((leg, i) => {
                const sign = leg.action === "BUY" ? 1 : -1;
                const mult = leg.type === "stock" ? 1 : 100;
                const cost = (leg.mid ?? 0) * mult * (leg.qty ?? 1) * sign;
                return (
                  <tr key={i} className="border-t border-white/[0.06]">
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={leg.action === "BUY" ? "text-[#00C805]" : "text-[#FF7A33]"}>
                        {leg.action}
                      </span>{" "}
                      <span className="text-white/70 capitalize">
                        {leg.qty} {leg.type}
                      </span>
                      {leg.quote_source && leg.type !== "stock" && (
                        <span
                          className="ml-1.5 text-[8.5px] uppercase tracking-[0.18em] px-1 py-px rounded"
                          style={
                            leg.quote_source === "live"
                              ? { color: "#00C805", background: "#00C80518" }
                              : { color: "#A98CFF", background: "#A98CFF14" }
                          }
                        >
                          {leg.quote_source}
                        </span>
                      )}
                      {leg.earnings_crossed && (
                        <span
                          className="ml-1 text-[8.5px] uppercase tracking-[0.18em] px-1 py-px rounded inline-flex items-center gap-0.5"
                          style={{ color: "#FF7A33", background: "#FF7A3318" }}
                          title="This leg's expiration is after the next earnings — expect IV crush"
                          data-testid="leg-earnings-tag"
                        >
                          ⚡ ER
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 num text-right">
                      {leg.type === "stock" ? "—" : Number(leg.strike).toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 num text-right text-white/70">
                      {leg.bid != null ? `$${Number(leg.bid).toFixed(2)}` : "—"}
                    </td>
                    <td className="px-2 py-1.5 num text-right font-medium">
                      ${Number(leg.mid ?? 0).toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 num text-right text-white/70">
                      {leg.ask != null ? `$${Number(leg.ask).toFixed(2)}` : "—"}
                    </td>
                    <td className="px-2 py-1.5 num text-right text-white/70">
                      {leg.iv != null ? `${(leg.iv * 100).toFixed(1)}%` : "—"}
                    </td>
                    <td className="px-2 py-1.5 num text-right text-white/70">
                      {leg.greeks?.delta != null ? Number(leg.greeks.delta).toFixed(2) : "—"}
                    </td>
                    <td
                      className={`px-2 py-1.5 num text-right font-medium ${
                        cost >= 0 ? "text-[#FFD60A]" : "text-[#00C805]"
                      }`}
                    >
                      {fmt$(cost)}
                    </td>
                    {lockedEntry && (() => {
                      const sig = `${leg.action}-${leg.type}-${leg.strike}-${leg.dte}`;
                      const snap = lockedBySig[sig];
                      if (!snap || snap.entry_mid == null) {
                        return (
                          <>
                            <td className="px-2 py-1.5 num text-right text-white/35">—</td>
                            <td className="px-2 py-1.5 num text-right text-white/35">—</td>
                          </>
                        );
                      }
                      const sign = leg.action === "BUY" ? 1 : -1;
                      const legPnl = (leg.mid - snap.entry_mid) * sign * 100 * (leg.qty || 1);
                      return (
                        <>
                          <td className="px-2 py-1.5 num text-right text-white/55">
                            ${Number(snap.entry_mid).toFixed(2)}
                          </td>
                          <td
                            className="px-2 py-1.5 num text-right font-medium"
                            style={{ color: legPnl >= 0 ? "#00C805" : "#FF3B30" }}
                          >
                            {fmt$(legPnl)}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!!breakevens?.length && (
        <div className="text-[11px] text-white/60">
          Breakeven{breakevens.length > 1 ? "s" : ""}:{" "}
          <span className="num text-[#00E5FF] font-medium">
            {breakevens.map((b) => `$${Number(b).toFixed(2)}`).join("  ·  ")}
          </span>
        </div>
      )}
    </div>
  );
};

export default GreeksTable;

